import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Swiper, SwiperSlide } from "swiper/react";
import { EffectFade, Autoplay } from "swiper/modules";
import { ArrowRight, MoveDown, Phone } from "lucide-react";
import "swiper/css";
import "swiper/css/effect-fade";

import MagneticButton from "./MagneticButton";
import { company } from "../data/site";
import useDynamicContent from "../hooks/useDynamicContent";
import useSiteSettings from "../hooks/useSiteSettings";

const SLIDES = [
  {
    img: "https://images.unsplash.com/photo-1770706240306-1010c2d321d7?crop=entropy&cs=srgb&fm=jpg&w=2400&q=90",
    sector: "Skyline",
    location: "Hyderabad · India",
  },
  {
    img: "https://images.unsplash.com/photo-1558573702-250d5cc26f70?crop=entropy&cs=srgb&fm=jpg&w=2400&q=90",
    sector: "Commercial",
    location: "HITEC City",
  },
  {
    img: "https://images.unsplash.com/photo-1598990891357-4ef3cd1f0f6d?crop=entropy&cs=srgb&fm=jpg&w=2400&q=90",
    sector: "Residential",
    location: "Jubilee Hills",
  },
  {
    img: "https://images.unsplash.com/photo-1705682434049-9776abe248fe?crop=entropy&cs=srgb&fm=jpg&w=2400&q=90",
    sector: "IT Campus",
    location: "Gachibowli",
  },
  {
    img: "https://images.unsplash.com/photo-1764885415563-8b868745e9e2?crop=entropy&cs=srgb&fm=jpg&w=2400&q=90",
    sector: "Healthcare",
    location: "Secunderabad",
  },
];

const wordReveal = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: 0,
    transition: { duration: 1, ease: [0.2, 0.8, 0.2, 1], delay: 0.35 + i * 0.12 },
  }),
};

export default function HeroSection() {
  const { content } = useDynamicContent('home');
  const { getSetting } = useSiteSettings();
  const [active, setActive] = useState(0);
  const swiperRef = useRef(null);
  const total = SLIDES.length;

  const heroTitle = content.hero_title || "Develop Your Land";
  const heroSubtitle = content.hero_subtitle || "Independently.";
  const heroDesc = content.hero_description || "Maximize your property's value without relying on external developers. At Hoflich, we offer a comprehensive, 360-degree solution to help you develop your land independently with 12 years of experience.";
  const phone = getSetting('company_phone', company.phone);

  // Preload images
  useEffect(() => {
    SLIDES.forEach((s) => {
      const img = new Image();
      img.src = s.img;
    });
  }, []);

  const goTo = (i) => swiperRef.current?.slideToLoop(i);

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden flex items-center"
    >
      {/* Swiper background */}
      <div className="absolute inset-0 z-0">
        <Swiper
          modules={[EffectFade, Autoplay]}
          effect="fade"
          fadeEffect={{ crossFade: true }}
          speed={1600}
          loop
          autoplay={{ delay: 6000, disableOnInteraction: false }}
          allowTouchMove={false}
          onSwiper={(s) => (swiperRef.current = s)}
          onSlideChange={(s) => setActive(s.realIndex)}
          className="!h-full !w-full"
        >
          {SLIDES.map((s, i) => (
            <SwiperSlide key={i} className="!h-full">
              <div
                data-testid={`hero-slide-${i}`}
                className="hero-slide-bg relative h-full w-full"
                style={{ backgroundImage: `url(${s.img})` }}
              />
            </SwiperSlide>
          ))}
        </Swiper>

        {/* Cinematic overlays (above swiper) */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/55 to-[#0F0F12] pointer-events-none" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/30 to-transparent pointer-events-none" />
        <div className="absolute inset-0 vignette pointer-events-none" />
        <div className="absolute inset-0 blueprint-grid opacity-[0.06] pointer-events-none" />
        <div className="absolute -left-32 top-1/2 -translate-y-1/2 h-[520px] w-[520px] orange-glow opacity-80 pointer-events-none" />
        <div className="absolute right-[-8%] bottom-[-12%] h-[600px] w-[600px] orange-glow opacity-50 pointer-events-none" />
      </div>

      {/* Top meta strip */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 0.8 }}
        className="hidden md:flex absolute top-28 inset-x-0 px-12 justify-between text-[11px] tracking-[0.36em] text-white/45 z-10"
      >
        <span>EST. {company.founded}</span>
        <span>HYDERABAD · INDIA</span>
        <span>DESIGN · APPROVALS · CONSTRUCTION · LOANS · SALES</span>
      </motion.div>

      {/* Slide caption (top-right of content area) */}
      <div className="absolute top-28 md:top-36 right-6 md:right-12 z-10 flex md:hidden">
        <SlideCaption slide={SLIDES[active]} />
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-12 pt-32 pb-28 md:pt-40 md:pb-32">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-end">
          <div className="lg:col-span-8">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2, duration: 0.8 }}
              className="flex items-center gap-4"
            >
              <span className="h-px w-12 bg-[#C96A2B]" />
              <span className="eyebrow">Hoflich · Land Development</span>
            </motion.div>

            <h1
              data-testid="hero-headline"
              className="font-display mt-8 text-[44px] sm:text-6xl md:text-7xl lg:text-[104px] xl:text-[120px] leading-[0.94] tracking-[-0.03em] max-w-[14ch]"
            >
              {[heroTitle].map((word, i) => (
                <span key={i} className="block overflow-hidden">
                  <motion.span
                    variants={wordReveal}
                    custom={i}
                    initial="hidden"
                    animate="show"
                    className="block"
                  >
                    {word}
                  </motion.span>
                </span>
              ))}
              <span className="block overflow-hidden">
                <motion.span
                  variants={wordReveal}
                  custom={2}
                  initial="hidden"
                  animate="show"
                  className="block"
                >
                  <span className="text-white/35 italic font-light pr-3 align-baseline">
                    —
                  </span>
                  <span className="text-[#C96A2B] italic font-light">{heroSubtitle}</span>
                </motion.span>
              </span>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.05, duration: 0.8 }}
              className="mt-10 max-w-xl text-white/65 text-[15px] md:text-[17px] leading-[1.75]"
            >
              {heroDesc}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.25, duration: 0.7 }}
              className="mt-12 flex flex-wrap gap-4 items-center"
            >
              <MagneticButton href="/consultation" testId="hero-cta-primary">
                Build independently
                <ArrowRight size={18} />
              </MagneticButton>
              <MagneticButton href="/services" variant="ghost" testId="hero-cta-secondary">
                Explore services
              </MagneticButton>
              <a
                href={`tel:${phone.replace(/\s/g, "")}`}
                data-testid="hero-phone"
                className="hidden md:inline-flex items-center gap-2 text-sm text-white/55 hover:text-[#C96A2B] transition-colors ml-2"
              >
                <Phone size={14} />
                {phone}
              </a>
            </motion.div>
          </div>

          {/* Right coverage panel */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.4, duration: 0.7 }}
            className="lg:col-span-4"
          >
            <div className="glass rounded-2xl p-6 md:p-8 max-w-sm ml-auto">
              <div className="hidden md:block mb-5">
                <SlideCaption slide={SLIDES[active]} />
              </div>
              <p className="eyebrow">Our Expertise</p>
              <div className="mt-5 grid grid-cols-3 gap-3">
                {["Design", "Approvals", "Funding", "Build", "Marketing", "Sales", "Quality", "12 Years", "360°"].map(
                  (k) => (
                    <span
                      key={k}
                      className="text-[10px] tracking-[0.1em] text-white/65 text-center py-2 rounded-md border border-white/8 bg-white/[0.03] hover:border-[#C96A2B] hover:text-[#C96A2B] transition-colors truncate"
                    >
                      {k}
                    </span>
                  )
                )}
              </div>
              <div className="mt-6 pt-5 border-t border-white/8 flex items-center justify-between">
                <div>
                  <p className="font-display text-3xl text-white">
                    12<span className="text-[#C96A2B]">+</span>
                  </p>
                  <p className="text-[10px] tracking-[0.3em] uppercase text-white/40 mt-1">
                    years
                  </p>
                </div>
                <div>
                  <p className="font-display text-3xl text-white">
                    100<span className="text-[#C96A2B]">+</span>
                  </p>
                  <p className="text-[10px] tracking-[0.3em] uppercase text-white/40 mt-1">
                    landowners
                  </p>
                </div>
                <div>
                  <p className="font-display text-3xl text-white">
                    360<span className="text-[#C96A2B]">°</span>
                  </p>
                  <p className="text-[10px] tracking-[0.3em] uppercase text-white/40 mt-1">
                    support
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Slider controls (bottom bar) */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.6, duration: 0.8 }}
        className="absolute bottom-8 md:bottom-10 inset-x-0 z-10 px-6 md:px-12"
      >
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-6">
          {/* Counter */}
          <div className="flex items-center gap-3 text-xs tracking-[0.3em] text-white/55">
            <span className="font-display text-white text-2xl">
              {String(active + 1).padStart(2, "0")}
            </span>
            <span className="h-px w-8 bg-white/20" />
            <span className="text-white/35">
              {String(total).padStart(2, "0")}
            </span>
          </div>

          {/* Dots */}
          <div
            className="flex items-center gap-2"
            data-testid="hero-slider-dots"
          >
            {SLIDES.map((_, i) => (
              <button
                key={i}
                onClick={() => goTo(i)}
                aria-label={`Go to slide ${i + 1}`}
                data-testid={`hero-dot-${i}`}
                className={`h-[3px] rounded-full transition-all duration-500 ${
                  active === i
                    ? "w-12 bg-[#C96A2B]"
                    : "w-6 bg-white/20 hover:bg-white/40"
                }`}
              />
            ))}
          </div>

          {/* Scroll cue */}
          <a
            href="#about"
            data-testid="hero-scroll-indicator"
            className="hidden md:inline-flex items-center gap-3 text-[10px] tracking-[0.36em] text-white/55 hover:text-white transition-colors"
          >
            <span>SCROLL</span>
            <motion.span
              animate={{ y: [0, 8, 0] }}
              transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
              className="inline-flex"
            >
              <MoveDown size={14} className="text-[#C96A2B]" />
            </motion.span>
          </a>
        </div>
      </motion.div>

      {/* Inline Ken Burns + crossfade styles */}
      <style>{`
        .hero-slide-bg {
          background-position: center;
          background-size: cover;
          background-repeat: no-repeat;
          transform: scale(1.08);
          transition: transform 7s linear;
        }
        .swiper-slide-active .hero-slide-bg {
          transform: scale(1.18) translate3d(-1.5%, -1%, 0);
        }
      `}</style>
    </section>
  );
}

function SlideCaption({ slide }) {
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={slide.location}
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -12 }}
        transition={{ duration: 0.55, ease: [0.2, 0.8, 0.2, 1] }}
        data-testid="hero-slide-caption"
        className="flex items-center gap-3"
      >
        <span className="h-1.5 w-1.5 rounded-full bg-[#C96A2B] shrink-0" />
        <div>
          <p className="text-[10px] tracking-[0.32em] uppercase text-[#C96A2B]">
            {slide.sector}
          </p>
          <p className="text-sm text-white/80 mt-0.5 font-display tracking-wide">
            {slide.location}
          </p>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
